import { Ref, useCallback, useRef, useState } from 'preact/hooks';

import { ContextMenu } from './ContextMenu';
import { useContextMenuGesture } from '#hooks/UNSAFE_useContextMenuGesture';
import { Coords } from '../../UNSAFE_Floating';

import { ContextMenuConfig } from '../../utils/UNSAFE_visTypes/common';
import { MenuProps, initialMenuProps } from './util';

export type gestureTypes = 'mouse'|'touch'|'keyboard'|null;
const TOUCH_OFFSET_VALUE = 30;

const menuPropGestureStates: Record<
  'mouse' | 'keyboard' | 'touch',
  Omit<Required<MenuProps>, 'anchorRef' | 'isOpen'>
> = {
  mouse: {
    initialFocus: 'menu',
    placement: 'bottom-start',
    offsetValue: 0
  },
  keyboard: {
    initialFocus: 'firstItem',
    placement: 'bottom-start',
    offsetValue: 0
  },
  touch: {
    initialFocus: 'menu',
    placement: 'end',
    offsetValue: TOUCH_OFFSET_VALUE
  }
};

type ContextMenuEventDetail = ({ gesture: 'mouse' | 'touch' } | { gesture: 'keyboard' }) & {
  target: Element;
};

type ContextMenuHandler<Context> = (detail: ContextMenuEventDetail) => {
  context: Context;
  elem: Element | null | undefined;
};

type ContextMenuOptions = {
  isDisabled?: boolean;
};
type Props<Context> = {
  onContextMenuHandler: ContextMenuHandler<Context>;
  contextMenuOptions: ContextMenuOptions;
  rootRef: Ref<HTMLElement>;
  contextMenuConfig?: ContextMenuConfig<Context>;
  onContextMenuDismissed?: (gesture: gestureTypes) => void;
};

export function useContextMenu<Context>({
  onContextMenuHandler,
  contextMenuOptions,
  rootRef,
  contextMenuConfig,
  onContextMenuDismissed
}: Props<Context>) {
  const [contextMenuContext, setContextMenuContext] = useState<Context>();

  const [menuProps, setMenuProps] = useState<MenuProps>(initialMenuProps);

  type ContextMenuEventDetail = (
    | { gesture: 'mouse' | 'touch'; anchor: Coords }
    | { gesture: 'keyboard'; anchor: Element }
  ) & { currentTarget: Element; target: Element };

  const gestureRef = useRef<gestureTypes>(null);
  const onVisContextMenuHandler = useCallback(
    ({ gesture, anchor, target }: ContextMenuEventDetail) => {
      gestureRef.current = gesture;
      const { context, elem } = onContextMenuHandler({
        gesture,
        target
      });
      setContextMenuContext(context);
      setMenuProps({
        ...menuPropGestureStates[gesture],
        anchorRef: { current: elem? elem : anchor },
        isOpen: true
      });
    },
    [onContextMenuHandler]
  );

  const contextMenuContent = contextMenuConfig && (
    <ContextMenu
      {...menuProps}
      onContextMenuDismissed={onContextMenuDismissed}
      gestureRef={gestureRef}
      setMenuProps={setMenuProps}
      rootRef={rootRef}
      contextMenuConfig={contextMenuConfig}
      contextMenuContext={contextMenuContext!}
    />
  );

  const { triggerProps } = useContextMenuGesture(onVisContextMenuHandler, contextMenuOptions);

  return { contextMenuProps: triggerProps, contextMenuContent };
}
